import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import axios from "axios";
import { ENV } from "../../util/Env";
import { ValidationPattern } from "../../util/ValidationPattern";
import Loader from "../../util/preloader/Preloader";
import DspFooter from "../events/DspFooter";
import DspHeader from "../events/DspHeader";
import NotificationModal from "../events/NotificationModal";
import "./QrRegister.css";

const GEO_OPTIONS = {
  enableHighAccuracy: true, // GPS-level accuracy where available
  timeout: 10000,           // 10 s max wait
  maximumAge: 0,            // never use a cached position
};

/** Resolves with { latitude, longitude } or null if denied/unavailable */
const getCurrentPosition = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      ({ coords }) =>
        resolve({
          latitude: parseFloat(coords.latitude.toFixed(6)),
          longitude: parseFloat(coords.longitude.toFixed(6)),
        }),
      () => resolve(null), // silently fall back — location is optional
      GEO_OPTIONS
    );
  });

const QrRegister = () => {
  const { Eid } = useParams();
  const [loading, setLoading] = useState(false);
  const [contactType, setContactType] = useState("mobile"); // "mobile" or "email"
  const [showModal, setShowModal] = useState(false);
  const [locationPermissionGranted, setLocationPermissionGranted] = useState(false);
  const [checkingLocation, setCheckingLocation] = useState(true);
  const [modalData, setModalData] = useState({
    type: "success",
    title: "Registration Successful",
    message: "You have been successfully registered for this event.",
  });
  const navigate = useNavigate();

  // Cache the position so we don't re-prompt if the user submits twice
  const locationRef = useRef(undefined);

  // Check location permission on component mount
  useEffect(() => {
    const checkLocationPermission = async () => {
      setCheckingLocation(true);
      const position = await getCurrentPosition();
      if (position) {
        locationRef.current = position;
        setLocationPermissionGranted(true);
      } else {
        setLocationPermissionGranted(false);
      }
      setCheckingLocation(false);
    };
    checkLocationPermission();
  }, []);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      name: "",
      mobileNo: "",
      email: "",
    },
  });

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      // Use cached location from permission check on mount
      const payload = {
        fullName: data.name.trim(),
        sessionId: Eid,
        ...(contactType === "mobile"
          ? { emailOrMobile: data.mobileNo.trim() }
          : { emailOrMobile: data.email.trim() }),
        ...(locationRef.current && {
          latitude: locationRef.current.latitude,
          longitude: locationRef.current.longitude,
        }),
      };

      const response = await axios.post(
        `web/api/v1/lms-web/admin/session/scanQrCodeForRegistrationOrAttendance`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (response.data && response.data.success) {
        setModalData({
          type: "success",
          title: "Operation completed successfully",
          message:
            response.data.message ||
            "You have been successfully registered for this event.",
        });
        setShowModal(true);
        reset();
      } else {
        throw new Error(
          response.data?.message || "Registration failed. Please try again."
        );
      }
    } catch (error) {

        const errorMessage =
            error?.response?.data?.message ||
            error?.data?.message ||
            error?.message ||
            'An error occurred during registration. Please try again.';

      setModalData({
        type: "error",
        title: "Operation Failed",
        message: errorMessage,
      });
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    if (modalData.type === "success") {
      navigate("/");
    }
  };

  return (
    <React.Fragment>
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="qr-register-main">
            <DspHeader />
            <div className="qr-register-container">
              <div className="qr-register-wrapper">
                {/* Left Section - Info */}
                {/* <div className="qr-register-info">
                  <h2 className="qr-register-title">Event Registration</h2>
                  <p className="qr-register-subtitle">
                    Register for the event using your details
                  </p>
                  <div className="qr-register-benefits">
                    <div className="benefit-item">
                      <span className="benefit-icon">✓</span>
                      <span className="benefit-text">Quick registration</span>
                    </div>
                    <div className="benefit-item">
                      <span className="benefit-icon">✓</span>
                      <span className="benefit-text">Instant confirmation</span>
                    </div>
                    <div className="benefit-item">
                      <span className="benefit-icon">✓</span>
                      <span className="benefit-text">
                        Event details sent to email
                      </span>
                    </div>
                  </div>
                </div> */}

                {/* Right Section - Form */}
                <div className="qr-register-form-section">
                <h4>Event Registration / Attendance</h4>
                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="qr-register-form"
                  >
                    {/* Full Name Field */}
                    <div className="form-group">
                      <label htmlFor="name" className="form-label">
                        Full Name <span className="required">*</span>
                      </label>
                      <Controller
                        name="name"
                        control={control}
                        rules={{
                          required: "Full Name is required",
                          minLength: {
                            value: 3,
                            message:
                              "Full Name must be at least 3 characters long",
                          },
                          maxLength: {
                            value: 60,
                            message:
                              "Full Name cannot exceed 60 characters",
                          },
                          pattern: {
                            value: ValidationPattern.name,
                            message:
                              "Full Name can only contain letters and spaces",
                          },
                        }}
                        render={({ field }) => (
                            <input
                              {...field}
                              id="name"
                              type="text"
                              className={`form-input ${
                                errors.name ? "input-error" : ""
                              }`}
                              placeholder="Enter full name"
                              disabled={loading}
                            />
                        )}
                      />
                      {errors.name && (
                        <span className="error-message">
                          {errors.name.message}
                        </span>
                      )}
                    </div>

                    {/* Contact Type Selection */}
                    <div className="form-group">
                      <div className="radio-group">
                        <div className="radio-option">
                          <input
                            type="radio"
                            id="contactMobile"
                            name="contactType"
                            value="mobile"
                            checked={contactType === "mobile"}
                            onChange={(e) => setContactType(e.target.value)}
                            disabled={loading}
                            className="radio-input"
                          />
                          <label htmlFor="contactMobile" className="radio-label">
                            Mobile
                          </label>
                        </div>
                        <div className="radio-option">
                          <input
                            type="radio"
                            id="contactEmail"
                            name="contactType"
                            value="email"
                            checked={contactType === "email"}
                            onChange={(e) => setContactType(e.target.value)}
                            disabled={loading}
                            className="radio-input"
                          />
                          <label htmlFor="contactEmail" className="radio-label">
                            Email
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Mobile Number Field - Conditional */}
                    {contactType === "mobile" && (
                      <div className="form-group">
                        <label htmlFor="mobileNo" className="form-label">
                          Mobile Number <span className="required">*</span>
                        </label>
                        <Controller
                          name="mobileNo"
                          control={control}
                          rules={{
                            required: "Mobile Number is required",
                            maxLength: {
                              value: 10,
                              message: "Mobile Number must be 10 digits",
                            },
                            pattern: {
                              value: ValidationPattern.mobileNew,
                              message:
                                "Please enter a valid mobile number",
                            },
                          }}
                          render={({ field }) => (
                            <input
                              {...field}
                              id="mobileNo"
                              type="tel"
                              className={`form-input ${
                                errors.mobileNo ? "input-error" : ""
                              }`}
                              placeholder="Enter mobile number"
                              maxLength="10"
                              disabled={loading}
                            />
                          )}
                        />
                        {errors.mobileNo && (
                          <span className="error-message">
                            {errors.mobileNo.message}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Email Field - Conditional */}
                    {contactType === "email" && (
                      <div className="form-group">
                        <label htmlFor="email" className="form-label">
                          Email <span className="required">*</span>
                        </label>
                        <Controller
                          name="email"
                          control={control}
                          rules={{
                            required: "Email is required",
                            pattern: {
                              value: ValidationPattern.email,
                              message: "Please enter valid email",
                            },
                          }}
                          render={({ field }) => (
                            <input
                              {...field}
                              id="email"
                              type="text"
                              className={`form-input ${
                                errors.email ? "input-error" : ""
                              }`}
                              placeholder="Enter your email address"
                              disabled={loading}
                            />
                          )}
                        />
                        {errors.email && (
                          <span className="error-message">
                            {errors.email.message}
                          </span>
                        )}
                      </div>
                    )}

                    

                    {/* Submit Button */}
                    <button
                      type="submit"
                      className="qr-register-btn"
                      disabled={loading || !locationPermissionGranted || checkingLocation}
                    >
                      {checkingLocation
                        ? "Checking location..."
                        : loading
                        ? "Submitting..."
                        : "Submit"}
                    </button>
                  </form>

                  {/* Location Permission Warning */}
                    {!checkingLocation && !locationPermissionGranted && (
                      <div className="location-permission-warning">
                        <p className="warning-icon">⚠️</p>
                        <p className="warning-text">
                          Location access is required. Please enable location
                          permission in your browser settings to proceed.
                        </p>
                      </div>
                    )}
                </div>
              </div>
            </div>
            <DspFooter />
          </div>

          {/* Success/Error Modal */}
          <NotificationModal
            isOpen={showModal}
            onClose={handleModalClose}
            type={modalData.type}
            title={modalData.title}
            message={modalData.message}
            buttonText={modalData.type === "success" ? "Back To Events" : "OK"}
          />
        </>
      )}
    </React.Fragment>
  );
};

export default QrRegister;
